import {
  OvertimeRule_Create,
  OvertimeRule_Read,
  OvertimeRule_Update,
  PrevailingWageFringeOvertimeTreatment,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { Nullable } from '~types/Nullable';

export type OvertimeRuleScope = OvertimeRule_Read['scope'];
export type OvertimeRuleFringeTreatment = OvertimeRule_Read['fringe_treatment'];

export interface OvertimeRuleFormData {
  name: string;
  scope: OvertimeRuleScope;
  dailyThresholdHours: Nullable<number>;
  weeklyThresholdHours: number;
  weeklyThresholdDays: Nullable<number>;
  multiplier: number;
  doubleTimeThresholdHours: Nullable<number>;
  doubleTimeMultiplier: Nullable<number>;
  fringeTreatment: OvertimeRuleFringeTreatment;
}

export class OvertimeRule {
  public static parse(proto: OvertimeRule_Read) {
    return new OvertimeRule(
      proto.id,
      proto.name,
      proto.scope,
      isNil(proto.daily_threshold_hours) ? null : Number(proto.daily_threshold_hours),
      Number(proto.weekly_threshold_hours),
      proto.weekly_threshold_days ?? null,
      Number(proto.multiplier),
      isNil(proto.double_time_threshold_hours)
        ? null
        : Number(proto.double_time_threshold_hours),
      isNil(proto.double_time_multiplier) ? null : Number(proto.double_time_multiplier),
      proto.fringe_treatment ?? PrevailingWageFringeOvertimeTreatment.STRAIGHT_TIME,
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public static deparse(data: OvertimeRuleFormData) {
    const result: OvertimeRule_Create = {
      name: data.name,
      scope: data.scope,
      daily_threshold_hours: data.dailyThresholdHours,
      weekly_threshold_hours: data.weeklyThresholdHours,
      weekly_threshold_days: data.weeklyThresholdDays,
      multiplier: data.multiplier,
      double_time_threshold_hours: data.doubleTimeThresholdHours,
      double_time_multiplier: data.doubleTimeMultiplier,
      fringe_treatment: data.fringeTreatment,
    };

    return result;
  }

  public static deparseUpdate(data: OvertimeRuleFormData) {
    const result: OvertimeRule_Update = {
      name: data.name,
      scope: data.scope,
      daily_threshold_hours: data.dailyThresholdHours,
      weekly_threshold_hours: data.weeklyThresholdHours,
      weekly_threshold_days: data.weeklyThresholdDays,
      multiplier: data.multiplier,
      double_time_threshold_hours: data.doubleTimeThresholdHours,
      double_time_multiplier: data.doubleTimeMultiplier,
      fringe_treatment: data.fringeTreatment,
    };

    return result;
  }

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get scope() {
    return this._scope;
  }

  public get dailyThresholdHours() {
    return this._daily_threshold_hours;
  }

  public get weeklyThresholdHours() {
    return this._weekly_threshold_hours;
  }

  public get weeklyThresholdDays() {
    return this._weekly_threshold_days;
  }

  public get multiplier() {
    return this._multiplier;
  }

  public get doubleTimeThresholdHours() {
    return this._double_time_threshold_hours;
  }

  public get doubleTimeMultiplier() {
    return this._double_time_multiplier;
  }

  public get fringeTreatment() {
    return this._fringe_treatment;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    private _id: string,
    private _name: string,
    private _scope: OvertimeRuleScope,
    private _daily_threshold_hours: Nullable<number>,
    private _weekly_threshold_hours: number,
    private _weekly_threshold_days: Nullable<number>,
    private _multiplier: number,
    private _double_time_threshold_hours: Nullable<number>,
    private _double_time_multiplier: Nullable<number>,
    private _fringe_treatment: OvertimeRuleFringeTreatment,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}
}
